import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AddressModel } from '../../components/partials/address/models/address.model';
import { BaseEntityResource } from '../../core/abstractions/base-entity.resource';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { PostValueResponseModel } from '../../models/core/post-value-response.model';
import { ResourceConfig } from '../../models/core/resource.config';
import { GmShareholderSummaryModel } from '../../models/gm-shareholder-summary.model';
import { GmShareholderModel } from '../../models/gm-shareholder.model';
import { ListGmShareholderModel } from '../../models/list-gm-shareholder.model';
import { ShareholderAccountModel } from '../../models/shareholder-account.model';
import { ShareholdersShareInfoModel } from '../../models/shareholders-share-info.model';
import { TopShareholderModel } from '../../models/top-shareholder.model';
import { TransactionModel } from '../../models/transaction.model';

@Injectable({
    providedIn: 'root'
})
export class GmShareholdersResource extends BaseEntityResource<GmShareholderModel, GmShareholderSummaryModel, ListGmShareholderModel> {
    constructor() {
        super(new ResourceConfig({ baseController: 'general-meetings/current/shareholders' }));
    }

    /**
     * Gets the top n shareholders sorted by amount of shares
     * @param quantity Number of top items to be returned.
     */
    getTop(quantity: number): Observable<ApiResponseModel<TopShareholderModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<TopShareholderModel[]>>(this.getUrl(`top/${quantity}`));
    }

    /**
     * Gets the accounts of a specific shareholder.
     * @param shareholderId ID of the shareholder.
     */
    getAccounts(shareholderId: string): Observable<ApiResponseModel<ShareholderAccountModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ShareholderAccountModel[]>>(this.getUrl(`${shareholderId}/accounts`));
    }

    /**
     * Gets the transactions of a specific shareholder
     * @param shareholderId ID of the shareholder.
     */
    getTransactions(shareholderId: string): Observable<ApiResponseModel<TransactionModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<TransactionModel[]>>(this.getUrl(`${shareholderId}/transactions`));
    }

    /**
     * Returns share statistics for a specific shareholder.
     * @param shareholderId Shareholder ID.
     */
    getShareStatistics(shareholderId: string): Observable<ApiResponseModel<ShareholdersShareInfoModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ShareholdersShareInfoModel[]>>(
            this.getUrl(`${shareholderId}/share-statistics`)
        );
    }

    /**
     * Updates the address of a shareholder.
     * @param shareholderId Shareholder ID.
     * @param shareholderAddress New address of the shareholder.
     */
    updateAddress(shareholderId: string, shareholderAddress: AddressModel): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePut<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`${shareholderId}/address`),
            shareholderAddress
        );
    }

    /**
     * Gets the address of a shareholder.
     * @param shareholderId Shareholder ID.
     */
    getAddress(shareholderId: string): Observable<ApiResponseModel<AddressModel>> {
        return this._requestService.makeGet<ApiResponseModel<AddressModel>>(this.getUrl(`${shareholderId}/address`));
    }
}
